import { Card, Button, Input, Label } from '@suscri/ui';
import {
  ArrowLeft,
  CreditCard,
  CheckCircle2,
  XCircle,
  Plug,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { trpc } from '../../lib/trpc';

// Configuración de Stripe desde el panel admin (sin redeploy).
// Guía para obtener los valores: docs/ALTA-STRIPE.md

export default function StripeConfigPage() {
  const navigate = useNavigate();
  const [token] = useState<string | null>(() =>
    localStorage.getItem('adminToken')
  );

  const [secretKey, setSecretKey] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [publishableKey, setPublishableKey] = useState('');
  const [feedback, setFeedback] = useState<{
    ok: boolean;
    text: string;
  } | null>(null);

  const { data: config, refetch } = trpc.admin.stripeGetConfig.useQuery(
    { token: token || '' },
    { enabled: Boolean(token) }
  );
  const setConfig = trpc.admin.stripeSetConfig.useMutation();
  const testConfig = trpc.admin.stripeTest.useMutation();

  if (!token) {
    return (
      <div className="p-6">
        No autorizado. Inicia sesión como administrador.
      </div>
    );
  }

  const save = async () => {
    setFeedback(null);
    try {
      await setConfig.mutateAsync({
        token,
        // Solo se envían los campos con contenido (los vacíos no tocan lo guardado)
        ...(secretKey ? { secretKey: secretKey.trim() } : {}),
        ...(webhookSecret ? { webhookSecret: webhookSecret.trim() } : {}),
        ...(publishableKey ? { publishableKey: publishableKey.trim() } : {}),
      });
      setSecretKey('');
      setWebhookSecret('');
      setPublishableKey('');
      await refetch();
      setFeedback({ ok: true, text: 'Configuración guardada.' });
    } catch (error) {
      setFeedback({
        ok: false,
        text: error instanceof Error ? error.message : 'No se pudo guardar.',
      });
    }
  };

  const test = async () => {
    setFeedback(null);
    const result = await testConfig.mutateAsync({ token });
    setFeedback({ ok: result.ok, text: result.message });
  };

  return (
    <div className="app-shell min-h-screen">
      {/* Header */}
      <div className="border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center gap-4 py-5">
            <Button
              variant="outline"
              onClick={() => navigate('/admin')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" /> Volver
            </Button>
            <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
              <CreditCard className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Pagos (Stripe)
              </h1>
              <p className="text-sm text-gray-600">
                Con las claves guardadas, planes y packs se cobran vía Stripe
                Checkout. Sin ellas, la app funciona en modo beta (activación
                directa).
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Estado actual */}
        <Card className="rounded-xl border-0 bg-white p-6 shadow-lg">
          <h3 className="text-base font-semibold text-gray-900 mb-4">
            Estado actual
          </h3>
          <div className="flex flex-wrap items-center gap-3 text-sm">
            {config?.configured ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1 font-semibold text-emerald-700">
                <CheckCircle2 className="h-4 w-4" /> Stripe ACTIVO
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-200 px-3 py-1 font-semibold text-amber-700">
                <XCircle className="h-4 w-4" /> Sin configurar — modo beta
                (activación directa)
              </span>
            )}
            {config?.mode && (
              <span
                className={`rounded-full px-3 py-1 font-semibold border ${
                  config.mode === 'live'
                    ? 'bg-red-50 border-red-200 text-red-700'
                    : 'bg-blue-50 border-blue-200 text-blue-700'
                }`}
              >
                Modo {config.mode.toUpperCase()}
              </span>
            )}
          </div>
          <div className="mt-4 space-y-1 text-sm text-gray-600">
            <p>
              Clave secreta:{' '}
              <code className="text-gray-800">
                {config?.secretKeyMasked ?? '— no guardada —'}
              </code>
            </p>
            <p>
              Secreto del webhook:{' '}
              <code className="text-gray-800">
                {config?.webhookSecretMasked ?? '— no guardado —'}
              </code>
            </p>
            <p>
              Clave publicable (opcional):{' '}
              <code className="text-gray-800">
                {config?.publishableKeyMasked ?? '— no guardada —'}
              </code>
            </p>
          </div>
          <p className="mt-4 text-xs text-gray-500">
            URL del webhook a dar de alta en Stripe:{' '}
            <code className="text-gray-700">{config?.webhookUrl}</code>
            <br />
            Eventos necesarios: <code>checkout.session.completed</code>,{' '}
            <code>invoice.paid</code>,{' '}
            <code>customer.subscription.deleted</code>
          </p>
        </Card>

        {/* Formulario */}
        <Card className="rounded-xl border-0 bg-white p-6 shadow-lg space-y-4">
          <h3 className="text-base font-semibold text-gray-900">
            Guardar claves{' '}
            <span className="font-normal text-gray-500">
              (cómo obtenerlas: <code>docs/ALTA-STRIPE.md</code>)
            </span>
          </h3>
          <div>
            <Label htmlFor="sk">Clave secreta (sk_test_… o sk_live_…)</Label>
            <Input
              id="sk"
              type="password"
              autoComplete="off"
              placeholder="sk_live_..."
              value={secretKey}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSecretKey(e.target.value)
              }
            />
          </div>
          <div>
            <Label htmlFor="whsec">Secreto del webhook (whsec_…)</Label>
            <Input
              id="whsec"
              type="password"
              autoComplete="off"
              placeholder="whsec_..."
              value={webhookSecret}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setWebhookSecret(e.target.value)
              }
            />
          </div>
          <div>
            <Label htmlFor="pk">Clave publicable (pk_…) — opcional</Label>
            <Input
              id="pk"
              autoComplete="off"
              placeholder="pk_live_..."
              value={publishableKey}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setPublishableKey(e.target.value)
              }
            />
          </div>
          <p className="text-xs text-gray-500">
            Los campos que dejes vacíos no modifican lo ya guardado. Las claves
            se almacenan en la base de datos interna (no viajan al repositorio).
          </p>
          <div className="flex gap-3">
            <Button
              onClick={save}
              disabled={
                setConfig.isPending ||
                (!secretKey && !webhookSecret && !publishableKey)
              }
            >
              {setConfig.isPending ? 'Guardando…' : 'Guardar'}
            </Button>
            <Button
              variant="outline"
              onClick={test}
              disabled={testConfig.isPending}
              className="flex items-center gap-2"
            >
              <Plug className="h-4 w-4" />
              {testConfig.isPending ? 'Probando…' : 'Probar conexión'}
            </Button>
          </div>
          {feedback && (
            <p
              className={`text-sm font-medium ${feedback.ok ? 'text-emerald-700' : 'text-red-700'}`}
            >
              {feedback.text}
            </p>
          )}
        </Card>

        {/* Stripe Tax (M7) */}
        <Card className="rounded-xl border-0 bg-white p-6 shadow-lg space-y-3">
          <h3 className="text-base font-semibold text-gray-900">
            Stripe Tax (impuestos automáticos)
          </h3>
          <p className="text-sm text-gray-600">
            Con Stripe Tax activado, el Checkout calcula el IVA según el país
            del cliente, pide la dirección de facturación y permite introducir
            el NIF-IVA de empresa.{' '}
            <strong>
              Actívalo primero en tu dashboard de Stripe (Configuración → Tax)
            </strong>
            ; si lo enciendes aquí sin activarlo allí, los pagos fallarán.
          </p>
          <label className="flex items-center gap-3 text-sm font-medium text-gray-800">
            <input
              type="checkbox"
              checked={config?.taxEnabled ?? false}
              onChange={async e => {
                setFeedback(null);
                try {
                  await setConfig.mutateAsync({
                    token,
                    taxEnabled: e.target.checked,
                  });
                  await refetch();
                  setFeedback({
                    ok: true,
                    text: e.target.checked
                      ? 'Stripe Tax activado en los checkouts.'
                      : 'Stripe Tax desactivado.',
                  });
                } catch (error) {
                  setFeedback({
                    ok: false,
                    text:
                      error instanceof Error
                        ? error.message
                        : 'No se pudo guardar.',
                  });
                }
              }}
              className="h-4 w-4 rounded border-gray-300"
            />
            Calcular impuestos automáticamente en los checkouts
          </label>
        </Card>
      </div>
    </div>
  );
}
